// Central content store for Travel Club Navigator Group.
// Edit these arrays to update reviews, guides, and packages.

export const QUICK_LINKS = [
  {
    title: "Timeshare Reviews",
    desc: "What really happens inside high-pressure presentations — and how to walk out with your assets intact.",
    to: "/timeshare-reviews",
    icon: "ShieldCheck",
    image:
      "https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    alt: "Two people shaking hands after a timeshare sales presentation",
    accent: "primary",
  },
  {
    title: "Travel Club Reviews",
    desc: "Transparent, no-spin breakdowns of Club Travalia, Ascendant Holidays, Aloha Travelers & Arrivia.",
    to: "/travel-club-reviews",
    icon: "Compass",
    image:
      "https://images.unsplash.com/photo-1758717152007-6a2eb7299409?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    alt: "Overwater bungalows lining a clear turquoise lagoon at a luxury resort",
    accent: "secondary",
  },
  {
    title: "Informational Guides",
    desc: "Step-by-step tutorials to master points, dodge rising maintenance fees, and book the best tiers.",
    to: "/guides",
    icon: "BookOpen",
    image:
      "https://images.unsplash.com/photo-1516546453174-5e1098a4b4af?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    alt: "Eyeglasses resting on a travel map while planning a trip",
    accent: "accent",
  },
  {
    title: "Vacation Packages",
    desc: "Curated, hassle-free escapes to the world's most exclusive destinations — inquire in seconds.",
    to: "/packages",
    icon: "Palmtree",
    image:
      "https://images.unsplash.com/photo-1702830499141-a0634d87d6af?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    alt: "Private balcony overlooking a bright blue ocean at a high-end resort",
    accent: "primary",
  },
];

// CLUBS moved to MongoDB — served via API (GET /api/clubs).

export const CLUB_PRESENTATIONS = {
  "ascendant-holidays": {
    name: "Ascendant Holidays",
    logo: "https://static.prod-images.emergentagent.com/jobs/f996d9bc-4940-4c43-ba67-952cc0af1833/images/000807a429338c4ce4fc6ba515a6aab0c945d9864284ae84bb831b00f35dde06.png",
    hero: {
      image:
        "https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
      alt: "Sales representative and couple across a table at an Ascendant Holidays presentation",
    },
    keywords: [
      "Ascendant Holidays Presentation",
      "Ascendant Holidays Review",
      "Ascendant Holidays Complaints",
      "Is Ascendant Holidays a Scam",
      "Timeshare Presentation Tips",
    ],
    intro:
      "Ascendant Holidays presentations are typically pitched as a 'discounted vacation' tour. Based on aggregated public reviews (BBB, Yelp, and complaint boards), attendees report a high-pressure sales environment, a product that behaves more like a timeshare than a 'vacation package,' and monthly costs well above the teaser figures. Here's what to expect — and how to protect yourself.",
    expectations: [
      {
        title: "The \"Discounted Vacation\" Tour",
        body: "You're usually invited via a heavily discounted stay or promotional offer, with attendance at a tour effectively mandatory to claim it. Plan for a lengthy, in-person session — not the quick meeting that's advertised.",
      },
      {
        title: "Vacation Package or Timeshare?",
        body: "Reviewers repeatedly note being told they were buying an 'all-included vacation package,' only to discover it functions as a long-term membership with ongoing dues and per-booking costs — closer to a timeshare.",
      },
      {
        title: "The Monthly-Payment Framing",
        body: "Pitches commonly cite roughly $80/month, but members report actual obligations of $200–$300+ per month once fees are added. Always ask for the true, all-in monthly figure in writing.",
      },
      {
        title: "The Same-Day Close",
        body: "Expect strong pressure to sign on the spot, with limited time to read the contract. The urgency is by design — a legitimate offer will still exist tomorrow.",
      },
    ],
    tactics: [
      {
        title: "\"All-Included\" Promises",
        body: "Claims of unlimited travel, discounted cruises, and easy availability frequently don't match reality. Members report paying hundreds of dollars per room to actually book, despite 'all-inclusive' promises.",
      },
      {
        title: "Anchored, Inflated Savings",
        body: "You'll hear about saving 'thousands.' In practice, reviewers found member rates matching — or exceeding — public sites like Expedia. Price-check before you believe the pitch.",
      },
      {
        title: "Deposit-on-Credit Pressure",
        body: "Deposits are often placed on a credit card during the meeting. Multiple complaints cite maxed cards and credit-score drops. Never let a five-figure decision hit your card under pressure.",
      },
      {
        title: "Post-Sale Upselling",
        body: "After joining, members describe scarce availability paired with pushes to buy more points or upgrades to 'unlock' better dates.",
      },
    ],
    protect: [
      "Ask for the true, all-in monthly cost in writing — not the $80 teaser.",
      "Get every 'all-included' and 'unlimited' claim documented before signing.",
      "Price-check member rates against Expedia/Booking.com in real time.",
      "Confirm the cancellation window (reviewers cite ~3 days) and the exact return process before you commit.",
      "Never allow a deposit on your credit card while under pressure.",
      "Send any cancellation in writing with delivery confirmation, and keep copies of everything.",
    ],
  },
  arrivia: {
    name: "Arrivia",
    logo: "https://static.prod-images.emergentagent.com/jobs/f996d9bc-4940-4c43-ba67-952cc0af1833/images/0589cb8a75635e6a8b0d83a9870e72279712668def4c5ee717cdcca483ab9856.png",
    hero: {
      image:
        "https://images.pexels.com/photos/5486096/pexels-photo-5486096.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=1600",
      alt: "Laptop showing an online webinar video call, typical of an Arrivia presentation",
    },
    keywords: [
      "Arrivia Presentation",
      "Arrivia Review",
      "Arrivia Complaints",
      "Is Arrivia a Scam",
      "Arrivia Free Cruise Voucher",
    ],
    intro:
      "Arrivia powers membership travel programs for many brands, and consumers most often encounter it through an online webinar presentation with a 'free cruise' hook. Based on aggregated public reviews (Trustpilot, BBB at ~1.6/5, and PissedConsumer), attendees report undelivered gifts, savings that fall far short of the claims, and difficult cancellations. Note: some negative experiences involve third-party affiliates using the Arrivia name.",
    expectations: [
      {
        title: "The Webinar / Zoom Presentation",
        body: "You'll typically join an online session. Advertised as ~45 minutes, reviewers report it running well over an hour before any 'gift' is discussed.",
      },
      {
        title: "The Free Cruise Voucher Hook",
        body: "A 3–7 night cruise voucher is dangled for attending. Numerous members report the voucher never arriving despite repeated emails and calls.",
      },
      {
        title: "The Wholesale-Pricing Pitch",
        body: "Expect claims of exclusive 'wholesale' rates and price guarantees. Be aware: affiliates sometimes misrepresent Arrivia; verify exactly who you're dealing with.",
      },
      {
        title: "The Membership Fee Structure",
        body: "You'll be presented with membership dues against 'lifetime savings.' Ask for the full fee schedule and cancellation terms up front.",
      },
    ],
    tactics: [
      {
        title: "Voucher Bait",
        body: "The free cruise or gift is the driver to attend. Some attendees report being removed from the webinar before ever receiving the promised voucher.",
      },
      {
        title: "Inflated Savings Claims",
        body: "You'll hear 'save thousands.' Reviewers report real savings closer to ~$200 on a week's trip — and sometimes prices higher than public booking sites.",
      },
      {
        title: "\"110% Price Guarantee\" Claims",
        body: "Guarantees pitched in some sessions are often unverifiable, and frequently tied to affiliates rather than Arrivia itself. Get any guarantee in writing.",
      },
      {
        title: "Hard-to-Cancel Memberships",
        body: "Members report being told they cannot cancel, refunds delayed 2+ months, and no clear dispute contact — document everything from the start.",
      },
    ],
    protect: [
      "Confirm whether you're dealing with Arrivia directly or a third-party affiliate using its name.",
      "Get the cruise-voucher terms and delivery timeline in writing before you attend or pay.",
      "Price-check the 'wholesale' rates against public sites in real time.",
      "Document every call and chat — note wait times and the absence of written confirmations.",
      "Read the cancellation and refund policy, and find the dispute contact, before joining.",
      "If you're charged after cancelling, request a chargeback from your bank and file with the BBB.",
    ],
  },
};

// TIMESHARES moved to MongoDB — served via API (GET /api/timeshares).

export const TIMESHARE = {
  hero: {
    image:
      "https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    alt: "Sales representative shaking hands with a couple at a timeshare presentation",
  },
  intro:
    "Timeshare presentations are engineered to be persuasive. We've sat through dozens so you know exactly what to expect, how the pressure builds, and how to walk out with your finances — and your weekend — fully intact.",
  expectations: [
    {
      title: "The 90-Minute Promise",
      body: "It's rarely 90 minutes. Presentations routinely stretch to 3–4 hours by design — the longer you stay, the more invested you feel. Set a hard stop before you arrive and share it up front.",
    },
    {
      title: "The Gift Hook",
      body: "Free resort credits, show tickets, or a discounted stay are the bait. They're real — but they're leverage. Accepting them creates a subtle sense of obligation the closer will use later.",
    },
    {
      title: "The Tour & The Dream",
      body: "You'll tour a model unit and talk about your dream vacations. This is intentional emotional priming before any numbers appear. Enjoy it, but keep your budget top of mind.",
    },
    {
      title: "The Closer Handoff",
      body: "When the friendly rep can't close, a 'manager' appears with a one-time-only price. This tiered handoff is a script, not a coincidence.",
    },
  ],
  tactics: [
    {
      title: "Artificial Urgency",
      body: "\"Today only\" pricing is the oldest play in the book. A legitimate investment will still be available tomorrow. Urgency is a red flag, not an opportunity.",
    },
    {
      title: "Anchoring the Price",
      body: "The first number is deliberately huge so every discount feels like a win. Ignore the anchor — evaluate the final total against your actual travel budget.",
    },
    {
      title: "Monthly-Payment Framing",
      body: "\"Just $299/month\" hides the true cost plus decades of maintenance fees. Always calculate the lifetime total, including fee inflation.",
    },
    {
      title: "The Peer-Pressure Table",
      body: "Open floors let you see others 'signing.' Some are staged. Never let a room's energy drive a five-figure decision.",
    },
  ],
  protect: [
    "Bring a pre-set budget in writing and don't exceed it — no matter the 'deal.'",
    "Never sign the same day. Ask to take documents home and read every line.",
    "Request the total lifetime cost including projected maintenance-fee increases.",
    "Know your state's rescission period — most give you 3–15 days to cancel in writing.",
    "Photograph or keep copies of every document and every verbal promise made.",
    "Say 'I never buy on the first visit' early — it disarms the urgency script.",
  ],
};

export const GUIDES = [
  {
    slug: "maximize-your-points",
    title: "How to Maximize Your Travel Club Points",
    category: "Points & Rewards",
    readTime: "7 min read",
    excerpt:
      "Stop leaving free nights on the table. A tactical playbook for stretching every point across seasons, tiers, and partners.",
    image:
      "https://images.unsplash.com/photo-1516546453174-5e1098a4b4af?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    alt: "Eyeglasses on a world map next to travel notes and colored pencils",
    sections: [
      {
        heading: "Understand your point 'seasons'",
        body: "Almost every club prices inventory by season — and the gap between low and peak weeks can be 3x. Shifting your trip by a single week can double what a point is worth. Pull your club's season calendar and plan travel around the value cliffs.",
      },
      {
        heading: "Bank and borrow strategically",
        body: "Most programs let you roll points forward one year or borrow from next year. Combine two years to unlock a premium suite you couldn't afford annually — just never let points expire unused, which is the #1 way members lose value.",
      },
      {
        heading: "Book the shoulder, not the peak",
        body: "Shoulder season (the weeks bracketing peak) gives you 80% of the weather at 50% of the points. It's the single highest-leverage habit of savvy members.",
      },
    ],
  },
  {
    slug: "avoid-rising-maintenance-fees",
    title: "How to Avoid Rising Maintenance Fees",
    category: "Fees & Costs",
    readTime: "6 min read",
    excerpt:
      "Maintenance fees creep 4–8% a year. Here's how to forecast them, cap the damage, and know when to walk.",
    image:
      "https://images.unsplash.com/photo-1766997249333-308fe0d24b8a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    alt: "Balcony view of a tropical beach and turquoise ocean at a resort",
    sections: [
      {
        heading: "Forecast the real cost",
        body: "Ask for the last five years of fee history before you ever join. Project that growth rate 10 years forward. A 'small' fee compounding at 7% nearly doubles in a decade — that's the number that actually matters.",
      },
      {
        heading: "Look for fee caps in writing",
        body: "Some contracts cap annual increases. If yours doesn't, that's negotiating leverage — or a reason to choose a more transparent club like the ones we review.",
      },
      {
        heading: "Know your exit options",
        body: "If fees outpace your usage, understand resale, deed-back, and transfer programs before you're locked in. A club that hides its exit path is telling you something.",
      },
    ],
  },
  {
    slug: "master-booking-tiers",
    title: "Mastering Booking Tiers & Windows",
    category: "Booking Strategy",
    readTime: "8 min read",
    excerpt:
      "The members who get the overwater villa aren't lucky — they understand the booking window. Here's the system.",
    image:
      "https://images.unsplash.com/photo-1758717152007-6a2eb7299409?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    alt: "Overwater bungalows at a luxury resort accessible through top booking tiers",
    sections: [
      {
        heading: "The booking window is everything",
        body: "Premium inventory opens to top tiers first — often 11–13 months out. If you're not booking the day your window opens, you're picking from leftovers. Set a calendar reminder for your exact window date.",
      },
      {
        heading: "Match your tier to your travel style",
        body: "Chasing the highest tier only pays off if you actually book far ahead and travel in peak weeks. Flexible, spontaneous travelers often get more value from a mid-tier plus last-minute deals.",
      },
      {
        heading: "Stack partner networks",
        body: "Many clubs share inventory through exchange networks. Learn which partners your club connects to — it can triple your effective destination list at no extra cost.",
      },
    ],
  },
  {
    slug: "using-your-travel-product",
    title: "New Member? How to Actually Use Your Product",
    category: "Getting Started",
    readTime: "5 min read",
    excerpt:
      "A friendly, step-by-step walkthrough for your first 30 days as a travel club member — from login to first booking.",
    image:
      "https://images.unsplash.com/photo-1603423697311-cfdc31203b62?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    alt: "Traveler relaxing at the edge of an infinity pool at a resort",
    sections: [
      {
        heading: "Week 1: Set up and explore",
        body: "Activate your member portal, confirm your point balance, and read your welcome kit cover to cover. Screenshot your tier, window dates, and fee schedule so they're always one tap away.",
      },
      {
        heading: "Week 2: Plan before you book",
        body: "List two or three dream destinations and check availability across different weeks. Don't book yet — you're learning how season and tier change the point cost.",
      },
      {
        heading: "Week 3–4: Make your first booking",
        body: "Book a shoulder-season trip within your window to see the full flow end to end. Your first booking is the best teacher — start small, then scale up to the big trips.",
      },
    ],
  },
];

export const PACKAGES = [
  {
    id: "maldives-overwater",
    name: "Maldives Overwater Escape",
    destination: "South Malé Atoll, Maldives",
    nights: 6,
    priceFrom: 3890,
    tag: "Bestseller",
    image:
      "https://images.unsplash.com/photo-1758717152007-6a2eb7299409?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    alt: "Overwater bungalows over a turquoise lagoon in the Maldives",
    highlights: ["Private overwater villa", "All-inclusive dining", "Sunset dolphin cruise"],
  },
  {
    id: "cancun-beachfront",
    name: "Cancún Beachfront Resort",
    destination: "Riviera Maya, Mexico",
    nights: 5,
    priceFrom: 1740,
    tag: "Family Favorite",
    image:
      "https://images.pexels.com/photos/20210498/pexels-photo-20210498.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=1200",
    alt: "Aerial view of beachfront resorts along the Cancún coastline",
    highlights: ["Swim-up suite", "Kids club included", "Cenote day trip"],
  },
  {
    id: "santorini-suite",
    name: "Santorini Caldera Suite",
    destination: "Oia, Santorini, Greece",
    nights: 5,
    priceFrom: 2960,
    tag: "Romance",
    image:
      "https://images.unsplash.com/photo-1672622851784-0dbd3df4c088?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    alt: "Iconic blue-domed churches glowing at sunset over the Aegean sea in Santorini",
    highlights: ["Private plunge pool", "Caldera-view balcony", "Sunset catamaran"],
  },
  {
    id: "bora-bora-sunset",
    name: "Bora Bora Sunset Retreat",
    destination: "Bora Bora, French Polynesia",
    nights: 7,
    priceFrom: 4620,
    tag: "Luxury",
    image:
      "https://images.unsplash.com/photo-1769149255642-c9741ad91b05?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    alt: "Infinity pool glowing at sunset at a Bora Bora luxury resort",
    highlights: ["Infinity-pool villa", "Private butler", "Lagoon snorkel safari"],
  },
  {
    id: "bali-cliffside",
    name: "Bali Cliffside Villa",
    destination: "Uluwatu, Bali, Indonesia",
    nights: 6,
    priceFrom: 2280,
    tag: "Wellness",
    image:
      "https://images.unsplash.com/photo-1766997249333-308fe0d24b8a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    alt: "Cliffside villa balcony overlooking a tropical beach in Bali",
    highlights: ["Daily spa credit", "Cliff-edge infinity pool", "Private chef breakfast"],
  },
  {
    id: "riviera-luxury",
    name: "Riviera Infinity Getaway",
    destination: "Côte d'Azur, France",
    nights: 5,
    priceFrom: 3350,
    tag: "New",
    image:
      "https://images.unsplash.com/photo-1713843841925-6af6ed0df472?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    alt: "Infinity pool overlooking the ocean at a French Riviera resort",
    highlights: ["Sea-view infinity pool", "Michelin dining credit", "Yacht day charter"],
  },
];

export const TESTIMONIALS = [
  {
    quote:
      "We almost signed a timeshare on the spot. Their presentation guide gave us the exact scripts to expect — we walked out clear-headed and saved thousands.",
    name: "Amara & Kwame O.",
    location: "Atlanta, GA",
    image:
      "https://images.unsplash.com/photo-1781456543175-b2c2438ba0d9?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    alt: "Couple in robes relaxing on a balcony overlooking the ocean",
  },
  {
    quote:
      "Finally, reviews that actually name names. The Aloha Travelers breakdown matched our experience to the letter. This is the transparency the industry needs.",
    name: "Mei L.",
    location: "Seattle, WA",
    image:
      "https://images.unsplash.com/photo-1603423697311-cfdc31203b62?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    alt: "Traveler relaxing in an infinity pool overlooking a green hillside",
  },
  {
    quote:
      "Booked the Maldives package through the inquiry form and the whole thing was stress-free. No hard sell, just a genuinely great trip.",
    name: "The Rivera Group",
    location: "Miami, FL",
    image:
      "https://images.unsplash.com/photo-1659882751445-a846ab85d6c6?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    alt: "Diverse group of friends walking together on a beach at golden hour",
  },
];

// ── New-member onboarding ("How to Use Your Membership") ──────────────────
export const MEMBER_ONBOARDING = {
  hero: {
    image:
      "https://images.unsplash.com/photo-1603423697311-cfdc31203b62?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    alt: "New member relaxing at the edge of an infinity pool overlooking a lush hillside",
  },
  intro:
    "Just bought a timeshare or travel club membership and feeling a little lost? You're not alone — the paperwork is dense and nobody really explains the next step. This is your plain-English, zero-stress roadmap from 'what did I just buy?' to booking your first dream trip.",
  steps: [
    {
      title: "Activate your member account",
      body: "Find your welcome email or member ID and log in to your club's portal. Set your password, confirm your contact info, and locate your dashboard. Screenshot your member number — you'll reference it constantly.",
    },
    {
      title: "Decode your membership documents",
      body: "Pull up your contract and highlight four things: your point/credit allotment, your tier level, your annual maintenance fee, and your booking window date. These four numbers run everything.",
    },
    {
      title: "Understand points, credits & tiers",
      body: "Your points are a currency and destinations are priced by season and tier. Learn what a 'peak' vs 'off' week costs so you never overspend points on a mediocre date.",
    },
    {
      title: "Learn your booking window",
      body: "Premium inventory opens to members on a specific date — often 9–13 months out. Put your exact window date in your calendar. Booking the day it opens is the single biggest advantage you have.",
    },
    {
      title: "Make your first booking (the easy way)",
      body: "Start small: book a shoulder-season getaway to see the full flow end to end. Search a destination, compare point costs across weeks, hold your dates, then confirm. Your first booking teaches you more than any brochure.",
    },
    {
      title: "Dodge the rookie mistakes",
      body: "Never let points expire, never book peak weeks by accident, and always read cancellation terms before you confirm. When in doubt, check our Guides — or ask us directly.",
    },
  ],
  faqs: [
    {
      q: "I bought a membership and have no idea where to start.",
      a: "Start with Step 1 above — activate your account and find your four key numbers. Everything else builds from there. It feels overwhelming for about a week, then it clicks.",
    },
    {
      q: "How do I actually book a trip?",
      a: "Log in to your member portal, search your destination, and compare the point cost across different weeks. Hold your preferred dates, review the total (including any fees), then confirm. We walk through it in detail on our Guides page.",
    },
    {
      q: "What are maintenance fees, and why do they keep rising?",
      a: "Maintenance fees cover resort upkeep and are separate from your purchase. They typically rise 4–8% a year. Read our guide on forecasting and capping them so there are no surprises.",
    },
    {
      q: "Can I bring guests or gift my week?",
      a: "Most clubs allow guests and some allow you to gift or rent your unused time — but the rules vary. Check your contract's guest and transfer clauses before you plan.",
    },
    {
      q: "What if I can't travel this year?",
      a: "Many programs let you bank or roll points forward one year, or deposit them into an exchange network. Do this before they expire — unused points are the #1 way members lose value.",
    },
    {
      q: "How do I exchange for a different resort?",
      a: "If your club belongs to an exchange network, you can trade your points for stays at partner resorts worldwide. Learn which networks your club connects to — it can triple your options.",
    },
  ],
};

// Brand
export const SITE_LOGO =
  "https://static.prod-images.emergentagent.com/jobs/f996d9bc-4940-4c43-ba67-952cc0af1833/images/b9301f559e0473cfe82a73501c9d102a0183219456c7550f6ef22469f70503de.png";

// ── Blog / editorial hub ──────────────────────────────────────────────────
// BLOG_POSTS moved to MongoDB — served via API (GET /api/blog-posts).

export const PARTNER = {
  hero: {
    image:
      "https://images.unsplash.com/photo-1540490190210-9e2825f75418?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    alt: "Aerial view of coconut palms lining a vivid turquoise shoreline",
  },
  audience: [
    { stat: "250K+", label: "Monthly readers researching clubs" },
    { stat: "68%", label: "Actively comparing memberships" },
    { stat: "4.8/5", label: "Reader trust rating" },
    { stat: "42K", label: "Newsletter subscribers" },
  ],
  tiers: [
    {
      name: "Verified Listing",
      price: "From $999/mo",
      featured: false,
      desc: "Establish credibility with high-intent travelers.",
      perks: [
        "Verified Partner badge on your review",
        "Enhanced, media-rich club profile",
        "Direct inquiry button to your team",
        "Quarterly performance report",
      ],
    },
    {
      name: "Sponsored Feature",
      price: "From $2,999/mo",
      featured: true,
      desc: "Positive, editorial-grade content that converts.",
      perks: [
        "Everything in Verified Listing",
        "Dedicated Partner Spotlight article",
        "Homepage & Blog featured placement",
        "Newsletter inclusion (42K readers)",
        "Clearly-labeled, SEO-optimized content",
      ],
    },
    {
      name: "Premium Partner",
      price: "Custom",
      featured: false,
      desc: "A full-funnel campaign built around your brand.",
      perks: [
        "Everything in Sponsored Feature",
        "Multi-article spotlight series",
        "Priority placement across the site",
        "Custom video & photo production",
        "Dedicated account strategist",
      ],
    },
  ],
  steps: [
    { title: "Tell us about your club", body: "Share your brand, destinations, and goals through the form below." },
    { title: "We build your content plan", body: "Our editorial team crafts a transparent, high-converting campaign." },
    { title: "Go live & grow", body: "Your content reaches high-intent travelers — and we track every result." },
  ],
};

